import { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import CreateIcon from "@mui/icons-material/Create";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Snackbar } from "@mui/material";
import { SearchTasks } from "./SearchTasks";
import { TasksListTable } from "./TasksListTable";
import { useGetAvailableTask } from "../hooks/useGetAvailableTask";
import { ButtonA } from "../../common/components/ButtonA";
import { SectionTitle } from "../../common/components/SectionTitle";
import { usePaginationTable } from "../../common/hooks/usePaginationTable";
import { TaskListToggle } from "./TaskListToggle";
import { useTasksListSearchValue } from "../hooks/useTasksListSearchValue";
import { useSetTasksListSearch } from "../hooks/useSetTasksListSearch";
import { defaultTasksListSearchState } from "../miscellaneous/tasksListSearchAtom";

export const TasksListManagement: FC = () => {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    clearErrors,
    reset,
  } = useForm();

  const location = useLocation();

  const searchAtomState = useTasksListSearchValue();
  const setSearchAtomState = useSetTasksListSearch();

  const [tasksSearch, setTasksSearchLocal] = useState(searchAtomState.criteria);

  const setTasksSearch = (newCriteria: any) => {
    setTasksSearchLocal(newCriteria);
    setSearchAtomState((prev) => ({ ...prev, criteria: newCriteria, hasSearched: true }));
  };

  const handleResetAtom = () => {
    setSearchAtomState(defaultTasksListSearchState);
  };

  const [isShowToastSuccess, setIsShowToastSuccess] = useState(false);

  const {
    availableTasks,
    totalRecords,
    setTotalRecords,
    setAvailableTask,
    mutationQueryAvailableTask,
  } = useGetAvailableTask();

  const {
    page,
    rowsPerPage,
    emptyRows,
    handleChangePage,
    handleChangeRowsPerPage,
    setPage,
    setRowsPerPage
  } = usePaginationTable(availableTasks);

  const navigate = useNavigate();

  useEffect(() => {
    setRowsPerPage(-1);
  }, []);

  useEffect(() => {
    if (location.state?.importSuccess) {
      setIsShowToastSuccess(true);
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  useEffect(() => {
    const queryParam = {
      pageSize: rowsPerPage,
      pageNum: page + 1,
      parentCompanyId: tasksSearch.parentCompanyId,
      companyId: tasksSearch.companyId,
      jobTitles: tasksSearch.jobTitles,
    };

    mutationQueryAvailableTask.mutate(queryParam);
  }, [
    page,
    rowsPerPage,
    JSON.stringify(tasksSearch)
  ]);

  const handleCloseToastSuccess = (
    _event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setIsShowToastSuccess(false);
  };

  return (
    <>
      <div className="mt-9">
        <div className="flex justify-between">
          <SectionTitle name="Task List Management" />
          <div className="flex flex-col items-center gap-2">
            <TaskListToggle activeMode="time-study" />
            <ButtonA
              name="Create New Template"
              icon={<CreateIcon />}
              customStyle={{
                backgroundColor: "#203C77",
                color: "#fff",
                borderRadius: "50px",
                width: "100%",
                fontWeight: 100,
                "&:hover": { backgroundColor: "#203C77" },
                textTransform: "capitalize",
              }}
              onHandleClick={() => {
                navigate("/create-new-template");
              }}
            />
          </div>
        </div>
        <div>
          <SearchTasks
            control={control}
            register={register}
            handleSubmit={handleSubmit}
            setValue={setValue}
            getValues={getValues}
            setTasksSearch={setTasksSearch}
            setPage={setPage}
            reset={reset}
            defaultValues={searchAtomState.criteria}
            onAtomReset={handleResetAtom}
          />
          <div className="mt-7">
            <TasksListTable
              data={availableTasks}
              page={page}
              rowsPerPage={rowsPerPage}
              totalRecords={totalRecords}
              setTotalRecords={setTotalRecords}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              setData={setAvailableTask}
              isLoading={mutationQueryAvailableTask.isLoading}
            />
          </div>
        </div>
      </div>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={isShowToastSuccess}
        autoHideDuration={8000}
        onClose={handleCloseToastSuccess}
      >
        <Alert
          onClose={handleCloseToastSuccess}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          <p>Import task list success</p>
        </Alert>
      </Snackbar>
    </>
  );
};
